"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { ArrowRight, QrCode, Banknote, Sparkles } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { TiltCard } from "./shared/tilt-card";
import { HeroDashboardMock } from "./hero-dashboard-mock";
import { HeroSubdomainChip } from "./hero-subdomain-chip";
import { MeshBackground } from "./shared/mesh-background";
import { MaskedLines } from "./fx/masked-lines";

// Stagger the eyebrow, copy and CTAs in one after another on load. A small
// delayChildren lets the headline lead the cascade, but kept short so nothing
// sits blank on first paint now that there's no preloader in front of it.
const container: Variants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.12
    }
  }
};
const item: Variants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.21, 0.5, 0.3, 1]
    }
  }
};
export const HeroSection = () => {
  const $ = _c(42);
  const reduce = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MeshBackground />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = reduce ? false : "hidden";
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <motion.span variants={item} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700"><Sparkles className="size-3.5 text-primary" />All-in-one gym management</motion.span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h1 className="mt-5 text-balance text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"><MaskedLines startDelay={0.1} lines={["Run your entire gym", <>from{" "}<span className="text-primary">one dashboard</span></>]} /></h1>;
    t4 = <motion.p variants={item} className="mt-5 max-w-xl text-pretty text-base text-muted-foreground sm:text-lg">Members, attendance, billing with QR invoices, payroll, point-of-sale and real financials — GymMart replaces the spreadsheets and runs your whole gym from one place, with your own private link to log in.</motion.p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Link href="/register" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Button nativeButton={false} size="lg" className="h-12 w-full px-6 text-base sm:w-auto" render={t5}>Start free<ArrowRight className="size-4" /></Button>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <motion.div variants={item} className="mt-8 flex flex-col gap-3 sm:flex-row">{t6}<Button nativeButton={false} size="lg" variant="outline" className="h-12 w-full px-6 text-base sm:w-auto" render={<a href="#showcase" />}>See it in action</Button></motion.div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <motion.div variants={item} className="mt-6"><HeroSubdomainChip /></motion.div>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t1) {
    t9 = <motion.div initial={t1} animate="visible" variants={container}>{t2}{t3}{t4}{t7}{t8}</motion.div>;
    $[8] = t1;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== reduce) {
    t10 = reduce ? false : {
      opacity: 0,
      y: 40
    };
    $[10] = reduce;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 1,
      y: 0
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    };
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== reduce) {
    t13 = reduce ? undefined : {
      y: [0, -12, 0]
    };
    $[14] = reduce;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut"
    };
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <TiltCard intensity={6}><HeroDashboardMock /></TiltCard>;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t13) {
    t16 = <motion.div animate={t13} transition={t14}>{t15}</motion.div>;
    $[18] = t13;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== reduce) {
    t17 = reduce ? undefined : {
      y: [0, 10, 0]
    };
    $[20] = reduce;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    };
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><QrCode className="size-4" /></span>;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="leading-tight"><p className="text-xs font-semibold">Invoice sent</p><p className="text-[11px] text-muted-foreground">QR receipt · emailed</p></div>;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t17) {
    t21 = <motion.div className="absolute -left-4 top-10 hidden items-center gap-2 rounded-xl border border-border/70 bg-card px-3 py-2 shadow-lg sm:flex" animate={t17} transition={t18}>{t19}{t20}</motion.div>;
    $[25] = t17;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== reduce) {
    t22 = reduce ? undefined : {
      y: [0, -10, 0]
    };
    $[27] = reduce;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      duration: 5.5,
      repeat: Infinity,
      ease: "easeInOut"
    };
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Banknote className="size-4" /></span>;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="leading-tight"><p className="text-xs font-semibold">Payroll run</p><p className="text-[11px] text-muted-foreground">Leave deducted</p></div>;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  let t26;
  if ($[32] !== t22) {
    t26 = <motion.div className="absolute -right-3 bottom-12 hidden items-center gap-2 rounded-xl border border-border/70 bg-card px-3 py-2 shadow-lg sm:flex" animate={t22} transition={t23}>{t24}{t25}</motion.div>;
    $[32] = t22;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== t10 || $[35] !== t16 || $[36] !== t21 || $[37] !== t26) {
    t27 = <motion.div initial={t10} animate={t11} transition={t12} className="relative">{t16}{t21}{t26}</motion.div>;
    $[34] = t10;
    $[35] = t16;
    $[36] = t21;
    $[37] = t26;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== t27 || $[40] !== t9) {
    t28 = <section id="home" className="relative overflow-hidden pt-16 pb-20 sm:pt-30 sm:pb-28">{t0}<div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8">{t9}{t27}</div></section>;
    $[39] = t27;
    $[40] = t9;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  return t28;
};